import { useCommerceOfferExposure } from "./useCommerceOfferExposure";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
    ApiCommerceStandaloneOfferV2,
    ApiCommerceStandalonePresentationCreateV2Req,
} from "@bridge-applications/pb-types";
import { useStandaloneOffers } from "./useStandaloneOffers";
import type { CommerceController } from "./useCommerce";
import {
    standaloneOfferAvailability,
    standalonePresentationKey,
} from "../helpers/standaloneOffers";
import { haptics } from "../helpers/haptics";

export function useBundleOffers(
    enabled: boolean,
    canAutoOpen: boolean,
    commerce: CommerceController,
) {
    const catalog = useStandaloneOffers(enabled);
    const [selection, setSelection] = useState<{
        userId: string | null;
        offerId: string;
    } | null>(null);
    const pendingRef = useRef(false);
    const lifecycleRef = useRef<AbortController | null>(null);
    const { purchaseOffer, resetCheckout } = commerce;
    const { recordPresentation, snapshot, clockOffsetMs, refresh, userId } =
        catalog;
    const now = Date.now() + clockOffsetMs;
    const offers =
        snapshot?.offers.filter(
            (offer) =>
                offer.presentation.dialog?.kind === "BUNDLE" &&
                standaloneOfferAvailability(offer, now) === "available",
        ) ?? [];
    const selected =
        selection?.userId === userId
            ? (snapshot?.offers.find(
                  (offer) =>
                      offer.offerId === selection.offerId &&
                      offer.presentation.dialog?.kind === "BUNDLE",
              ) ?? null)
            : null;

    useEffect(() => {
        const lifecycle = new AbortController();
        lifecycleRef.current = lifecycle;
        return () => {
            lifecycle.abort();
            pendingRef.current = false;
        };
    }, [userId]);

    const open = useCallback(
        (
            offer: ApiCommerceStandaloneOfferV2,
            trigger: ApiCommerceStandalonePresentationCreateV2Req["trigger"] = "MANUAL",
        ) => {
            if (
                !enabled ||
                pendingRef.current ||
                commerce.mutationStatus !== "idle" ||
                standaloneOfferAvailability(
                    offer,
                    Date.now() + clockOffsetMs,
                ) !== "available"
            )
                return;
            resetCheckout();
            setSelection({ userId, offerId: offer.offerId });
            void recordPresentation(offer, trigger);
            if (trigger === "MANUAL") {
                haptics.impact("light");
                void refresh().catch(() => {});
            }
        },
        [
            enabled,
            commerce.mutationStatus,
            clockOffsetMs,
            userId,
            resetCheckout,
            recordPresentation,
            refresh,
        ],
    );

    useEffect(() => {
        if (
            !canAutoOpen ||
            selected ||
            catalog.loadStatus !== "ready" ||
            document.visibilityState !== "visible"
        )
            return;
        const offer = offers.find(
            (candidate) =>
                !catalog.shownKeys.includes(
                    standalonePresentationKey(candidate),
                ),
        );
        if (offer) open(offer, "OFFER_APP_LOAD");
    }, [
        canAutoOpen,
        selected,
        catalog.loadStatus,
        catalog.shownKeys,
        offers,
        open,
    ]);

    const close = useCallback(() => {
        if (pendingRef.current) return;
        setSelection(null);
        resetCheckout();
    }, [resetCheckout]);

    const purchase = useCallback(async () => {
        const signal = lifecycleRef.current?.signal;
        if (
            !selected ||
            !snapshot ||
            !signal ||
            signal.aborted ||
            pendingRef.current ||
            catalog.loadStatus !== "ready" ||
            commerce.mutationStatus !== "idle" ||
            standaloneOfferAvailability(
                selected,
                Date.now() + clockOffsetMs,
            ) !== "available"
        )
            return;
        pendingRef.current = true;
        haptics.impact("light");
        let fulfilled = false;
        try {
            await purchaseOffer(
                selected,
                undefined,
                () => {
                    fulfilled = true;
                },
                "BUNDLE_OFFER",
                { termsVersion: snapshot.termsVersion, refresh },
            );
        } catch {
            if (!fulfilled && !signal.aborted) {
                haptics.notification("error");
                // Stale price/terms/eligibility errors become retryable only after a new server read.
                await refresh().catch(() => {});
            }
        } finally {
            if (!signal.aborted) {
                pendingRef.current = false;
                if (fulfilled) {
                    haptics.notification("success");
                    setSelection(null);
                }
            }
        }
    }, [
        selected,
        snapshot,
        catalog.loadStatus,
        commerce.mutationStatus,
        clockOffsetMs,
        purchaseOffer,
        refresh,
    ]);

    useCommerceOfferExposure(
        selected,
        "BUNDLE_OFFER",
        selected !== null,
        userId,
    );

    return {
        offers,
        selected,
        open,
        close,
        purchase,
        clockOffsetMs,
        isBusy:
            commerce.mutationStatus !== "idle" ||
            catalog.loadStatus === "loading",
        availability:
            catalog.loadStatus === "ready"
                ? standaloneOfferAvailability(selected, now)
                : ("unavailable" as const),
        error: catalog.error ?? commerce.error,
    };
}
